/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

/**
 * Helpers for mapping the internal locale identifier (hyphenated, e.g. `en-gb`)
 * to and from its two-segment URL path form (e.g. `en/gb`). The identifier stays
 * hyphenated everywhere internally; only the URL is split into two segments.
 *
 * Kept dependency-free (no localization/i18n imports) so it is safe to pull into
 * client components and hooks without dragging in native-only modules.
 */

/**
 * Converts an internal locale identifier into its two-segment URL path form.
 * @example localeToPath('en-gb') // 'en/gb'
 */
export const localeToPath = (locale: string): string =>
  locale.replace('-', '/');

/**
 * Reconstructs the internal locale identifier from the two URL path segments.
 * @example pathToLocale('en', 'gb') // 'en-gb'
 */
export const pathToLocale = (locale: string, country: string): string =>
  `${locale}-${country}`;

/**
 * Builds an app-relative path (no basePath) for a product-tile page or for home.
 * The LOB slug leads, ahead of the locale/country pair, matching the legacy site.
 *
 * @example lobPath('en-gb', 'snap') // '/snap/en/gb'
 * @example lobPath('en-gb')         // '/en/gb'
 */
export const lobPath = (locale: string, lobSlug?: string): string =>
  lobSlug ? `/${lobSlug}/${localeToPath(locale)}` : `/${localeToPath(locale)}`;
